import { designVariables } from "@studio3t/design-variables";
import {
  BadgeWarningSubtle,
  Icon,
  IconSVG,
  SVGColorKey,
  Tooltip,
} from "@studio3t/generic-components";
import {
  ControlButton,
  type Node,
  type NodeProps,
  type Viewport,
} from "@xyflow/react";
import {
  type MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowNode,
  EncodingBadge,
  HEADER_ICON,
  PipelineFlowCanvas,
  RESOURCE_ICON,
  SERVICE_ICON,
} from "@/pages/PipelineDetail/PipelineFlow";
import {
  buildPipelineGraph,
  type StageNodeData,
} from "@/pages/PipelineDetail/pipelineGraph";
import { StageCardBadges } from "@/pages/PipelineDetail/StageCardBadges";
import {
  StageBody,
  StageCardBox,
  StageCardLink,
  StageHeader,
  StageHeaderLeft,
  StageLabelText,
  StageRow,
  StageRowText,
} from "@/pages/PipelineDetail/styles";
import type { Connector } from "@/types/api";
import { withPlaceholderRefs } from "./emptySlotHints";
import {
  buildPipelineCode,
  shownPipelineCode,
  stageErrorsByNodeId,
} from "./pipelineCode";
import {
  collectPipelineRefs,
  fetchRefLookups,
  refErrors,
  validatePipelineDoc,
} from "./pipelineLint";
import { type StagedSchema, usePipelineStaging } from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";

// The Full pipeline view's graph strip (Figma 14419-264437, under the code
// editor): /studio's Output-tab pipeline preview (PipelineVisualization)
// rebuilt on the /pipelines React Flow canvas. Same logic as the old div
// chart — live view of the pipeline doc (the code draft when one exists,
// else the staged sections), schema badges on the cards, unresolved-service
// flags from the engine's service list — with /pipelines' full canvas:
// layout, pan/zoom, dotted background, controls and minimap.

type StageNodeType = Node<StageNodeData, "stage">;

// The card tooltip: the lint errors when known, else the generic flag.
function cardTitle(data: StageNodeData): string | undefined {
  if (data.errors) return data.errors.join("\n");
  return data.unresolved ? "Service unresolved" : undefined;
}

// /pipelines' stage card plus the schema badge the old /studio chart had
// (schema_id was carried in the graph data but never rendered there) and
// /studio's "⚠ Unresolved" chip for stages the lint pass flags — the
// tooltip lists the actual errors. Unresolved cards keep the normal solid
// border here (only the chip flags them); the amber dashed `$unresolved`
// treatment stays a /pipelines-detail thing. The back link labels this
// page "Studio".
// The card's face: header strip, service and resource rows, the
// unresolved chip and the schema badge.
function StageCardContent({ data }: { data: StageNodeData }) {
  const { type } = data;
  return (
    <>
      <StageHeader $type={type}>
        <StageHeaderLeft>
          <IconSVG
            icon={HEADER_ICON[type]}
            size={16}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageLabelText>{data.label}</StageLabelText>
        </StageHeaderLeft>
        {data.encoding && <EncodingBadge>{data.encoding}</EncodingBadge>}
      </StageHeader>
      <StageBody>
        <StageRow>
          <IconSVG
            icon={SERVICE_ICON[type]}
            size={14}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageRowText>{data.serviceName}</StageRowText>
        </StageRow>
        <StageRow>
          <IconSVG
            icon={RESOURCE_ICON[type]}
            size={14}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
          <StageRowText $regular>{data.resource}</StageRowText>
          {/* Inline at the row end — a third text row would clip. */}
          {data.unresolved && (
            <CardUnresolvedBadge>
              <IconSVG
                icon={Icon.Warning}
                size={11}
                svgColorKey={SVGColorKey.warningIcon}
              />
              Unresolved
            </CardUnresolvedBadge>
          )}
        </StageRow>
        {/* The schema the stage refs and the operations it takes, on one
            row under the resource. */}
        <StageCardBadges data={data} />
      </StageBody>
    </>
  );
}

function StudioStageNode({ data }: NodeProps<StageNodeType>) {
  const { type } = data;
  const { pathname, search } = useLocation();
  // No service page behind a service that doesn't exist: the card stays
  // put and the tooltip carries the lint errors.
  if (data.serviceKnown === false) {
    return (
      <StageCardBox $type={type} $backed={data.backed} title={cardTitle(data)}>
        <StageCardContent data={data} />
      </StageCardBox>
    );
  }
  return (
    <StageCardLink
      $type={type}
      $backed={data.backed}
      to={`/services/${encodeURIComponent(data.serviceName)}`}
      state={{ from: { pathname: pathname + search, label: "Studio" } }}
      title={cardTitle(data)}
    >
      <StageCardContent data={data} />
    </StageCardLink>
  );
}

const studioNodeTypes = { stage: StudioStageNode, arrow: ArrowNode };

// The graph sits under the code editor, and the uncontrolled canvas
// remounts per content change — trail the editor by a pause so typing
// doesn't rebuild the canvas (and flash the invalid-JSON state) on every
// keystroke.
function useDebouncedGraphCode(liveCode: string) {
  const [code, setCode] = useState(liveCode);
  useEffect(() => {
    if (code === liveCode) return;
    const timer = window.setTimeout(() => setCode(liveCode), 400);
    return () => window.clearTimeout(timer);
  }, [liveCode, code]);
  return [code, setCode] as const;
}

// Unresolved cards, /studio's concept widened to the shared lint pass:
// structural problems plus reference checks (unknown services, resources,
// schemas) and the placeholder warnings, grouped per stage node — a card
// stays unresolved until its entry is filled in. The lookups ride the TTL
// caches; unavailable lookups skip their checks, so a dead API never
// paints false warnings.
function useStageErrors(doc: Partial<Connector> | undefined) {
  const [stageErrors, setStageErrors] = useState<Map<string, string[]>>(
    new Map(),
  );
  // The registered services the lookup answered with (every referenced
  // one when the service API is down); null until it answers.
  const [knownServices, setKnownServices] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (doc === undefined) return;
    let active = true;
    (async () => {
      const refs = collectPipelineRefs(doc);
      const lookups = await fetchRefLookups(refs);
      if (!active) return;
      setKnownServices(new Set(lookups.providers.keys()));
      setStageErrors(
        stageErrorsByNodeId([
          ...validatePipelineDoc(doc),
          ...refErrors(refs, lookups),
        ]),
      );
    })();
    return () => {
      active = false;
    };
  }, [doc]);
  return { stageErrors, knownServices };
}

// A reset (paste) restarts the graph on the pasted text, whole-graph fit.
// While unconsumed, the reset overrides the debounced code as pure derived
// data — the canvas must never pair the old code with a cleared viewport,
// and flushing state mid-render proved unreliable across React's batches
// (the update could be discarded). The effect then consumes it: drops the
// carried viewport/focus and aligns the debounced state with the text.
function useGraphReset(
  graphReset: { token: number; text: string } | null,
  viewportRef: MutableRefObject<Viewport | null>,
  focusFittedRef: MutableRefObject<string | null>,
  setCode: (text: string) => void,
) {
  const consumedResetRef = useRef(graphReset?.token ?? 0);
  const resetActive =
    graphReset !== null && graphReset.token !== consumedResetRef.current;
  useEffect(() => {
    if (!resetActive) return;
    if (!graphReset) return;
    consumedResetRef.current = graphReset.token;
    viewportRef.current = null;
    focusFittedRef.current = null;
    setCode(graphReset.text);
  }, [resetActive, graphReset, viewportRef, focusFittedRef, setCode]);
  return {
    resetActive,
    resetText: resetActive ? (graphReset?.text ?? null) : null,
    resetToken: graphReset?.token ?? 0,
  };
}

// The parsed pipeline doc, or the parse error for the "Invalid JSON" state.
function parsePipelineDoc(code: string): {
  doc?: Partial<Connector>;
  error?: string;
} {
  // The empty Full view (ghost template) has no document yet — the strip
  // shows its "add stages" line, not an invalid-JSON error. Only the
  // exactly-empty string counts as empty here, matching the linter
  // (doc.length === 0) and buildWorkspacePayload (=== ""); whitespace-only
  // text is invalid JSON and gets the syntax squiggle like everywhere else.
  if (code === "") return {};
  try {
    return { doc: JSON.parse(code) as Partial<Connector> };
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

// The graph builder resolves stage refs through the doc's schemas block
// into the entry id (block resource); the staged list turns that into the
// schema's real display name. Hand-written docs without a block fall back
// the same way on the raw ref.
function nameSchemaBadges(
  nodes: ReturnType<typeof buildPipelineGraph>["nodes"],
  schemas: StagedSchema[],
) {
  return nodes.map((n) =>
    n.type === "stage" && n.data.schemaId
      ? {
          ...n,
          data: {
            ...n.data,
            schemaName:
              schemas.find(
                (s) =>
                  s.id === `schema:${n.data.schemaName ?? n.data.schemaId}`,
              )?.name ?? n.data.schemaName,
          },
        }
      : n,
  );
}

// Stamp the lint verdicts onto the built stage cards.
type GraphNode = ReturnType<typeof buildPipelineGraph>["nodes"][number];

// One card's lint verdict and whether its service exists.
function flagNode(
  node: GraphNode,
  stageErrors: Map<string, string[]>,
  knownServices: Set<string> | null,
): GraphNode {
  if (node.type !== "stage") return node;
  const serviceKnown =
    knownServices === null || knownServices.has(node.data.serviceName);
  const errors = stageErrors.get(node.id);
  return {
    ...node,
    data: {
      ...node.data,
      serviceKnown,
      ...(errors ? { unresolved: true, errors } : {}),
    },
  };
}

function flagNodes(
  nodes: GraphNode[],
  stageErrors: Map<string, string[]>,
  knownServices: Set<string> | null,
): GraphNode[] {
  return nodes.map((node) => flagNode(node, stageErrors, knownServices));
}

// Fourth button in the +/−/fit column (fit already doubles as the view
// reset). ControlButton is a plain <button>, so title/aria pass through.
function AutoFocusControl({
  autoFocus,
  onToggle,
}: {
  autoFocus: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip
      text={
        autoFocus
          ? "Disable automatic canvas movement"
          : "Enable automatic canvas movement"
      }
      side="right"
    >
      <AutoFocusToggle
        $active={autoFocus}
        onClick={onToggle}
        aria-pressed={autoFocus}
        data-testid="graph-toggle-autofocus"
      >
        <IconSVG
          icon={Icon.Move}
          size={12}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </AutoFocusToggle>
    </Tooltip>
  );
}

export function PipelineGraphView() {
  const {
    sources,
    transforms,
    sinks,
    schemas,
    pipelineCodeDraft,
    focusedStageId,
    graphReset,
    pipelineExtras,
  } = usePipelineStaging();
  // Whether the code cursor may move the canvas (zoom-to-card); the
  // in-canvas toggle turns it off for hands-off editing.
  const [autoFocus, setAutoFocus] = useState(true);
  // Survive the uncontrolled canvas's remounts: the viewport carries over
  // so a settled edit never moves the camera, and the fitted-focus memory
  // stops FocusNode from re-zooming to a card it's already on.
  const viewportRef = useRef<Viewport | null>(null);
  const focusFittedRef = useRef<string | null>(null);

  const generated = useMemo(
    () =>
      buildPipelineCode(
        { sources, transforms, sinks, schemas },
        pipelineExtras,
      ),
    [sources, transforms, sinks, schemas, pipelineExtras],
  );
  const { editsLocked, opening } = useStudioPipeline();
  // The code editor's document (shownPipelineCode): nothing to draw while a
  // pipeline is on its way in.
  const liveCode = shownPipelineCode(
    opening,
    pipelineCodeDraft,
    generated,
    editsLocked,
  );
  const [code, setCode] = useDebouncedGraphCode(liveCode);

  const reset = useGraphReset(graphReset, viewportRef, focusFittedRef, setCode);
  const effectiveCode = reset.resetText ?? code;

  const parsed = useMemo(
    () => parsePipelineDoc(effectiveCode),
    [effectiveCode],
  );

  const { stageErrors, knownServices } = useStageErrors(parsed.doc);

  // /studio's Output panel showed "Invalid JSON: …" while the pipeline
  // config didn't parse.
  if (parsed.error !== undefined) {
    return (
      <GraphEmpty data-testid="pipeline-graph-view">
        Invalid JSON: {parsed.error}
      </GraphEmpty>
    );
  }

  // Empty "" references show their placeholder labels on the cards (the
  // same labels the editor draws as ghost hints); the errors above are
  // computed on the real document.
  const { nodes } = buildPipelineGraph(
    parsed.doc ? withPlaceholderRefs(parsed.doc) : null,
  );
  const flagged = flagNodes(
    nameSchemaBadges(nodes, schemas),
    stageErrors,
    knownServices,
  );

  // Partial docs render whatever stages exist (like the old /studio chart);
  // only a doc with no stages at all has nothing to draw. While a pipeline
  // is on its way in the strip stays quiet — the hint would flash before
  // the loaded code lands (TTTL-191 open flash).
  if (flagged.length === 0) {
    return (
      <GraphEmpty data-testid="pipeline-graph-view">
        {opening ? null : "Add pipeline stages to see the graph"}
      </GraphEmpty>
    );
  }

  return (
    <GraphPanel data-testid="pipeline-graph-view">
      <PipelineFlowCanvas
        nodes={flagged}
        nodeTypes={studioNodeTypes}
        focusNodeId={autoFocus ? focusedStageId : null}
        focusFittedRef={focusFittedRef}
        // An unconsumed reset always starts at the whole-graph fit.
        initialViewport={
          reset.resetActive ? undefined : (viewportRef.current ?? undefined)
        }
        onViewportChange={(viewport) => {
          viewportRef.current = viewport;
        }}
        // The canvas is uncontrolled, so it must also remount when the
        // async lint verdicts land or change — and on a reset, even when
        // the pasted content is identical.
        canvasKey={`${reset.resetToken}:${JSON.stringify([...stageErrors])}:${effectiveCode}`}
        controlsExtra={
          <AutoFocusControl
            autoFocus={autoFocus}
            onToggle={() => setAutoFocus((on) => !on)}
          />
        }
      />
    </GraphPanel>
  );
}

// The auto-movement toggle reads as a proper on/off control instead of a
// dimmed button: ON sits on a solid pressed-gray (a darker neutral on
// hover), OFF is the plain white control — no transparency in either
// state, and the glyph keeps its regular colour throughout.
const TOGGLE_ON_BACKGROUND = designVariables.primitives.neutrals["50"];
const TOGGLE_ON_HOVER_BACKGROUND = designVariables.primitives.neutrals["60"];

const AutoFocusToggle = styled(ControlButton)<{ $active: boolean }>`
  ${(p) =>
    p.$active &&
    `
    &.react-flow__controls-button {
      background: ${TOGGLE_ON_BACKGROUND};
    }
    &.react-flow__controls-button:hover {
      background: ${TOGGLE_ON_HOVER_BACKGROUND};
    }
  `}
`;

const GraphPanel = styled.div`
  width: 100%;
  height: 100%;
  background: ${(p) => p.theme.editorBackground};

  /* /pipelines' minimap at its native 200×150 would swallow the strip —
     shrink it, anchored to its bottom-right panel corner. */
  .react-flow__minimap {
    transform: scale(0.6);
    transform-origin: bottom right;
  }
`;

// Zero/error state in the editors' quiet placeholder voice.
const GraphEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  background: ${(p) => p.theme.editorBackground};
`;

// The "⚠ Unresolved" chip on the proper design-system badge (the old
// /studio chart used a hand-rolled span). Inline-flex centers the icon
// with the label; the text colour is pinned to the badge token so it
// doesn't inherit the card link's colour.
const CardUnresolvedBadge = styled(BadgeWarningSubtle)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  color: ${(p) => p.theme.badgeWarningSubtleColor};
`;
